import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Popper,
  ClickAwayListener,
  Grid2,
  Paper,
  Autocomplete,
  Stack,
  TextField,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import { CalendarToday } from '@mui/icons-material';
import { StatusProps } from '../../../shared/constants';
import { CloseIcon, DownIcon, UpIcon } from '../../../shared/components/AppIcons';
import { PostQueryParams, StatusPropType } from '../../../../data/types/contentMarketing.interface';

const useStyles = makeStyles({
  contentSection: {
    padding: '17px 17px 14px 17px',
    width: '514px',
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  save: {
    width: '127px',
    height: '40px',
    fontSize: '18px !important',
    backgroundColor: '#F67570 !important',
    boxShadow: 'none !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  toggleButtonContainer: {
    '& label': {
      marginLeft: 0,
    },
    '& span.MuiSwitch-root': {
      marginTop: '-12px',
    },
  },
  bold: {
    fontFamily: 'Lato !important',
    color: 'rgba(0, 0, 0, 0.87) !important',
  },
  formControlLabels: {
    '& .MuiTypography-root': {
      fontFamily: 'Lato !important',
    },
  },
  textField: {
    '& .MuiInputBase-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
      padding: '4px 8px',
      backgroundColor: '#fff',
      height: 'auto',
      minHeight: '40px',
      marginBottom: '4px',
    },
    '& .MuiInputBase-input': {
      padding: '6px 0',
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '0 !important',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
    '& Chip': {
      border: '1px solid ##E1E3E8',
      color: '#5F6774',
      backgroundColor: '#FAFAFA',
    },
  },
  radioBtn: {
    '& span': {
      '& svg': {
        fill: '#E1E3E8 !important',
      },
    },
    '& .Mui-checked': {
      '& svg': {
        fill: '#30343F !important',
      },
    },
  },
  dateField: {
    fontFamily: 'Lato !important',
    '& .react-datepicker-wrapper': {
      padding: '0 8px 0 0',
      width: '49%',
      height: '40px',
      '& .MuiFormControl-root-MuiTextField-root': {
        height: '40px',
      },
      '& input': {
        height: '8px !important',
      },
    },
    '& .react-datepicker-popper': {
      left: '-36px !important',
      '& .react-datepicker__triangle': {
        marginLeft: '27px',
      },
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

type Props = {
  open: boolean;
  anchorEl: null | HTMLElement;
  filters: PostQueryParams;
  handleClose: () => void;
  handleFilters: (filters: PostQueryParams) => void;
  trigger?: string;
};

const FilterModal: React.FC<Props> = ({ handleFilters, open, anchorEl, filters, handleClose, trigger }) => {
  const classes = useStyles();
  const [openDropDown, setOpenDropDown] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<StatusPropType[]>(StatusProps.filter((status) => filters.statuses?.includes(status.value)));
  const [startDate, setStartDate] = useState<Date | string>(filters.startDate || '');
  const [endDate, setEndDate] = useState<Date | string>(filters.endDate || '');

  const defaultStatusProps = {
    options: StatusProps,
    getOptionLabel: (option: StatusPropType) => option.value,
  };

  const handleStatusChange = (e: React.SyntheticEvent<Element, Event>, values: StatusPropType[]) => {
    setSelectedStatuses(values);
  };

  const handleApply = () => {
    handleFilters({
      statuses: selectedStatuses.map((status) => status.name),
      startDate: startDate || '',
      endDate: endDate || '',
      perPage: 5,
      page: 1,
    });
  };

  const handleCancel = () => {
    setSelectedStatuses([]);
    setStartDate('');
    setEndDate('');
    handleFilters({
      statuses: [],
      startDate: '',
      endDate: '',
      perPage: 5,
      page: 1,
    });
    handleClose();
  };

  return (
    <Popper
      open={open}
      aria-modal="true"
      role="dialog"
      anchorEl={anchorEl}
      placement="bottom-end"
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper variant="elevation" className={classes.contentSection}>
          <Grid2 container direction="column">
            <Grid2 container fontWeight="bold">
              <span>Filter By</span>
            </Grid2>

            <Grid2 mt={2}>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  backgroundColor: '#E1E3E8',
                  paddingBottom: '1px',
                }}
              />
            </Grid2>

            {trigger === 'dashboard' && <Grid2 container direction="row" mt={2}>
              <label>Status</label>
              <Stack spacing={1} sx={{ width: '100%' }} className={classes.textField}>
                <Autocomplete
                  {...defaultStatusProps}
                  id="auto-highlight"
                  autoHighlight
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusChange}
                  renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                  onOpen={() => setOpenDropDown(true)}
                  onClose={() => setOpenDropDown(false)}
                  popupIcon={
                    openDropDown ? (
                      <UpIcon className={classes.dropdownIcon} />
                    ) : (
                      <DownIcon className={classes.dropdownIcon} />
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          label={option.name}
                          sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                          size="small"
                          deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                          {...tagProps}
                        />
                      );
                    })
                  }
                />
              </Stack>
            </Grid2>}

            <Grid2 container direction="column" width="101%">
              <label>Date Added</label>
              <Grid2 container className={classes.dateField} width="104%">
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="MM/dd/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarToday fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="MM/dd/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarToday fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
              </Grid2>
            </Grid2>

            <Grid2>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  backgroundColor: '#E1E3E8',
                  paddingBottom: '1px',
                }}
              />
            </Grid2>

            <Grid2 container mt={2} justifyContent="end">
              <Button size="small" variant="text" type="button" className={classes.cancel} onClick={handleCancel}>
                cancel
              </Button>
              <Button size="small" className={classes.save} variant="contained" onClick={handleApply}>
                <span style={{ fontSize: 'h5', textTransform: 'none' }}>Apply Filters</span>
              </Button>
            </Grid2>
          </Grid2>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default FilterModal;
